/**
 * CollectionTargetBar — this month's credit collection target vs. what has
 * actually come in, as a progress bar with a "where the month is" pace marker.
 *
 * Rendered in the Credit sheet's summary band (Credit.jsx).
 *
 * WHY IT REFETCHES INSTEAD OF SUMMING THE ROWS IN VIEW
 * The neighbouring stat cards derive from `filteredDataArray`, so they follow
 * the sheet's filters. This one deliberately does not, because the target has a
 * definition that the sheet's rows cannot reproduce:
 *
 *   - The Credit sheet only carries StatusFlag='Credit'. The collection query
 *     spans 'Credit' AND 'CreditCleared' — a deal cleared on the 10th still owed
 *     an installment this month and still contributed the money that paid it.
 *     Dropping it would understate both halves of the ratio.
 *   - The sheet has no per-row "paid this month"; its TotalPaid is life-to-date.
 *
 * So it reads /getcreditcollectiontrend, the same endpoint behind the
 * dashboard's Credit Collection Overview card, and reports the same figures a
 * manager sees there. Two screens disagreeing about the month's collection
 * would be worse than a bar that ignores a filter.
 */
import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GlobalContext } from './GlobalContext';
import { apiFetch } from './api.js';
import { formatUGX } from './UtilityFunctions';
import { DEFAULT_COLLECTION_MONTHS, currentMonthRow } from './creditCollection';

//Semantic status colors are never tenant-themed (see MODAL_STYLE.md, and the
//same note in components/DebtorRiskPanel.jsx) — a bar that says "behind" has to
//read as behind under every theme preset.
const AHEAD = '#059669';   // emerald 600
const BEHIND = '#d97706';  // amber 600

//FIXED WIDTH, NOT flex-1 — and this is the whole point of the 2026-09-22 pass.
//This used to be `flex-1 min-w-0 max-w-[360px]` in a fixed-height, non-wrapping
//row whose other three children are rigid (a button, a grid of fixed cards, the
//density toggle). That made it the ONLY child able to absorb a shortfall, so it
//took 100% of every deficit: 258px at 1600, 98px at 1440, 24px at 1366 and
//LITERALLY ZERO from 1340 down to 1024 — `min-w-0` is what let it reach zero
//rather than bottoming out at its content. The one element on the band that
//reports performance rather than state was the first thing the layout threw
//away, on the widths most people actually use.
//
//So it is rigid now and the stat cards yield instead (they drop out at
//breakpoints — see CreditSalesStatsBar in Credit.jsx), and .sheet-actions-bar
//scrolls at every width rather than only under 768px.
//
//TWO SIZES, because the band itself has two. It is h-14 (56px) on a phone and
//md:h-[80px] above that, and the roomy desktop card measures 75px — which fit
//the 80px band and hung 9px out of the top and 10px out of the bottom of the
//56px one. Every figure here is sized off whichever band it is in: the phone
//card comes out at ~49px. Check both if you change any of the spacing.
const SHELL = 'flex-none w-[272px] md:w-[316px] rounded-lg border border-gray-200'
    + ' bg-white px-3 py-1 md:py-2';

export default function CollectionTargetBar() {
    const { DealershipID } = useContext(GlobalContext);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['creditCollectionTrend', DealershipID, DEFAULT_COLLECTION_MONTHS],
        queryFn: async () => {
            const response = await apiFetch('/getcreditcollectiontrend', {
                body: { DealershipID, months: DEFAULT_COLLECTION_MONTHS },
            });
            if (!response.ok) throw new Error(`Server responded with status: ${response.status}`);
            const result = await response.json();
            return result.data;
        },
        enabled: Boolean(DealershipID),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
    });

    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const dayOfMonth = now.getDate();
    const pacePct = (dayOfMonth / daysInMonth) * 100;

    // Matched by 'YYYY-MM' key, not by position in the series — see
    // creditCollection.js for why position drifts.
    const row = currentMonthRow(data, now);

    // Due + overdue, the same total the dashboard's "Total" category shows: an
    // installment missed in June is still money the team is chasing in August,
    // and the team is measured on collecting it.
    const target = (Number(row?.DueTarget) || 0) + (Number(row?.OverdueTarget) || 0);
    const collected = (Number(row?.DueCollected) || 0) + (Number(row?.OverdueCollected) || 0);

    // A dealership that has never filled in MonthlyPayment has no target, and
    // a percentage of zero is not a number worth rendering.
    const hasTarget = target > 0;
    const pct = hasTarget ? (collected / target) * 100 : 0;
    const fillPct = Math.min(pct, 100);
    const onTrack = pct >= Math.min(pacePct, 100);
    const color = onTrack ? AHEAD : BEHIND;

    if (isLoading) {
        return (
            <div className={`${SHELL} animate-pulse`} id="credit-collection-target">
                <div className="h-3 w-28 rounded bg-gray-100" />
                <div className="mt-1 md:mt-2 h-4 md:h-5 w-32 rounded bg-gray-100" />
                <div className="mt-1 md:mt-2 h-1.5 md:h-2 w-full rounded bg-gray-100" />
            </div>
        );
    }

    //A failed side panel must not take the sheet's summary band with it.
    if (isError || !row) return null;

    //THE EXACT FIGURES LEAD, because the card now shows them abbreviated. Two
    //shillings' precision is not what this band is for, but "UGX 104.5M" is the
    //only place either number appears on the screen and someone reconciling
    //against a bank statement needs the rest of the digits without leaving the
    //sheet. The definitions follow, unchanged.
    const tooltip = hasTarget
        ? `Collected ${formatUGX(collected)} of ${formatUGX(target)}.\n\n`
          + `Target: the monthly installment agreed on every credit deal that was still owing this month, `
          + `including deals already past their due date and deals cleared during the month.\n`
          + `Collected: credit repayments (AR) dated this month.\n`
          + `Same figures as the dashboard's Credit Collection Overview.`
        : `Collected ${formatUGX(collected)}.\n\n`
          + 'No target for this month — no open credit deal has a Monthly Payment set.';

    return (
        <div className={SHELL} id="credit-collection-target" title={tooltip}>
            {/* Row 1: what this is, and how far through the month we are. The
                pace note moved up here off the bottom row so that the figure
                below it has the whole of row 2 to itself. */}
            <div className="flex items-baseline justify-between gap-2 leading-none">
                <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                    Collected this month
                </span>
                <span className="text-[11px] whitespace-nowrap text-gray-400">
                    Day {dayOfMonth}/{daysInMonth}
                </span>
            </div>

            {/* Row 2: THE MONEY, at the same size as the Metric on the stat
                cards beside it. Previously the largest thing in this component
                was the words "Collected this month" and the figure was an 11px
                footnote, which is most of why it read as a caption rather than
                as a number.

                The figure stays near-black and the PERCENTAGE carries the
                ahead/behind colour. Colouring the money itself was the other
                option and is worse: amber on the collected total reads as
                "this amount is wrong" rather than "this amount is behind
                pace", and a dealership that is behind most months would get a
                permanently alarmed-looking headline figure. */}
            <div className="mt-0.5 md:mt-1 flex items-baseline gap-2 leading-none">
                <span className="text-base md:text-lg font-semibold tabular-nums text-gray-900">
                    {formatUGX(collected, { abbreviate: true })}
                </span>
                {hasTarget && (
                    <span className="text-xs font-bold tabular-nums" style={{ color }}>
                        {Math.round(pct)}%
                    </span>
                )}
                <span className="ml-auto truncate text-[11px] tabular-nums text-gray-500">
                    {hasTarget
                        ? `of ${formatUGX(target, { abbreviate: true, symbol: false })}`
                        : 'no target set'}
                </span>
            </div>

            <div
                className="relative mt-0.5 md:mt-1.5 h-1.5 md:h-2 w-full overflow-hidden rounded bg-gray-100"
                role="progressbar"
                aria-label="Credit collected this month against target"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pct)}
            >
                <div
                    className="h-full rounded transition-all duration-500"
                    style={{ width: `${fillPct}%`, background: color }}
                />
                {/* Pace marker: how far through the month we are. Without it a
                    bare "58%" is unreadable — good on the 22nd, alarming on the
                    2nd. Sits above the fill so it stays visible once passed.
                    Held just short of the end because on the last day of the
                    month pacePct is exactly 100, and a 1px rule at left:100%
                    falls outside the track and is clipped away by
                    overflow-hidden — losing the marker on the one day it matters
                    most. */}
                {hasTarget && (
                    <div
                        className="absolute top-0 h-full w-px bg-gray-600/70"
                        style={{ left: `${Math.min(pacePct, 99.5)}%` }}
                    />
                )}
            </div>
        </div>
    );
}
